// ─────────────────────────────────────────────────────────────────────────────
// BIZPLUS PARITY — GST registrations  (Phase 4.4, multi-GSTIN)
//
// A business registers separately in every state it operates from and files a
// SEPARATE return per registration. This screen is where those registrations
// live, so nothing about filing depends on editing a file on the server.
//
// The branch mapping is the load-bearing part: it decides which return each
// invoice lands in. An unmapped branch is surfaced on the filing screen as
// "unattributed" rather than quietly folded into the primary.
// ─────────────────────────────────────────────────────────────────────────────

import { useState, useEffect } from 'react';
import { SettingsSubNav } from '../components/SettingsSubNav';
import { Receipt, CheckCircle2, Star, AlertTriangle, Plus } from 'lucide-react';

interface Registration {
  _id: string;
  gstin: string;
  legalName: string;
  tradeName?: string;
  stateCode: string;
  branchIds?: string[];
  isPrimary: boolean;
  isActive: boolean;
  grossTurnover?: number;
}

interface Location {
  _id: string;
  name: string;
  code?: string;
}

export function GstRegistrationsSettingsPage() {
  const [items, setItems] = useState<Registration[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [gstin, setGstin] = useState('');
  const [legalName, setLegalName] = useState('');
  const [tradeName, setTradeName] = useState('');
  const [grossTurnover, setGrossTurnover] = useState('');
  const [branchIds, setBranchIds] = useState<string[]>([]);

  const fetchAll = () => {
    setLoading(true);
    fetch('/api/gstr/registrations')
      .then((res) => res.json())
      .then((data) => setItems(Array.isArray(data.items) ? data.items : []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));

    fetch('/api/accounting/org/locations')
      .then((res) => res.json())
      .then((data) => setLocations(Array.isArray(data.data) ? data.data : []))
      .catch(() => setLocations([]));
  };

  useEffect(fetchAll, []);

  const flash = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const res = await fetch('/api/gstr/registrations', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        gstin, legalName, tradeName, branchIds,
        grossTurnover: Number(grossTurnover) || 0,
      }),
    });
    const data = await res.json();
    if (!res.ok) {
      // Shown inline, not as an alert: the GSTIN checksum message tells the user
      // exactly what to re-type.
      setError(data.error || 'Could not add the registration.');
      return;
    }
    setShowModal(false);
    setGstin(''); setLegalName(''); setTradeName(''); setGrossTurnover(''); setBranchIds([]);
    flash('GST registration added.');
    fetchAll();
  };

  const makePrimary = async (id: string) => {
    const res = await fetch(`/api/gstr/registrations/${id}/primary`, { method: 'POST' });
    const data = await res.json();
    if (!res.ok) return flash(data.error || 'Could not change the primary.');
    flash('Primary registration updated.');
    fetchAll();
  };

  const setActive = async (id: string, isActive: boolean) => {
    const res = await fetch(`/api/gstr/registrations/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ isActive }),
    });
    const data = await res.json();
    if (!res.ok) return flash(data.error || 'Could not update the registration.');
    flash(isActive ? 'Registration reactivated.' : 'Registration deactivated.');
    fetchAll();
  };

  const locName = (id: string) =>
    locations.find((l) => String(l._id) === String(id))?.name || 'Unknown branch';

  const mappedBranchIds = new Set(items.flatMap((r) => (r.branchIds || []).map(String)));
  const unmappedLocations = locations.filter((l) => !mappedBranchIds.has(String(l._id)));

  return (
    <div className="text-slate-900 dark:text-slate-100">
      <SettingsSubNav />

      <div className="max-w-[1580px] mx-auto space-y-6 pb-16">
        {toast && (
          <div className="p-4 bg-success/10 dark:bg-success border border-success/25 dark:border-success/40 rounded-xl text-success-text dark:text-success-text text-sm flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-success-text" />
            {toast}
          </div>
        )}

        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h2 className="text-lg font-bold flex items-center gap-2">
              <Receipt className="w-5 h-5 text-primary" />
              GST Registrations
            </h2>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
              One registration per state you are registered in. Each files its own GSTR-1 and GSTR-2.
            </p>
          </div>
          <button
            onClick={() => { setError(null); setShowModal(true); }}
            className="px-4 py-2 rounded-lg bg-primary text-on-primary text-sm font-semibold flex items-center gap-2 shrink-0"
          >
            <Plus className="w-4 h-4" /> Add registration
          </button>
        </div>

        {/* An unmapped branch means its invoices belong to no return. Said here,
            where it can be fixed, not only at filing time. */}
        {!loading && items.length > 0 && unmappedLocations.length > 0 && (
          <div className="p-4 bg-warning/10 dark:bg-warning border border-warning/25 dark:border-warning/40 rounded-xl text-sm text-warning-text dark:text-warning-text flex gap-3">
            <AlertTriangle className="w-5 h-5 shrink-0 text-warning-text" />
            <div>
              <p className="font-semibold">
                {unmappedLocations.length} branch(es) are not mapped to a registration.
              </p>
              <p className="text-xs mt-1">
                Their invoices fall to the primary registration. If any of them trade under a
                different GSTIN, map them — otherwise those supplies are filed in the wrong state.
              </p>
              <p className="text-xs mt-1 opacity-80">
                {unmappedLocations.map((l) => l.name).join(', ')}
              </p>
            </div>
          </div>
        )}

        {loading ? (
          <p className="text-sm text-slate-500">Loading…</p>
        ) : items.length === 0 ? (
          <div className="p-8 text-center border border-dashed border-slate-300 dark:border-slate-700 rounded-xl">
            <p className="text-sm text-slate-500">
              No GST registration yet. Add the one you file under to enable GSTR-1 and GSTR-2.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((r) => (
              <div
                key={r._id}
                className={`p-4 rounded-xl border flex flex-col md:flex-row md:items-center gap-4 justify-between ${
                  r.isActive
                    ? 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700'
                    : 'bg-slate-100 dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 opacity-70'
                }`}
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono font-bold text-sm">{r.gstin}</span>
                    {r.isPrimary && (
                      <span className="px-2 py-0.5 rounded-full bg-primary/10 text-primary text-[11px] font-bold flex items-center gap-1">
                        <Star className="w-3 h-3" /> Primary
                      </span>
                    )}
                    {!r.isActive && (
                      <span className="px-2 py-0.5 rounded-full bg-slate-200 dark:bg-slate-700 text-[11px] font-semibold">
                        Inactive
                      </span>
                    )}
                    <span className="px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-[11px] font-semibold">
                      State {r.stateCode}
                    </span>
                  </div>
                  <p className="text-sm mt-1">{r.legalName}</p>
                  <p className="text-xs text-slate-500 mt-1">
                    {(r.branchIds || []).length
                      ? `Branches: ${(r.branchIds || []).map(locName).join(', ')}`
                      : 'No branches mapped — receives anything not mapped elsewhere.'}
                  </p>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  {!r.isPrimary && r.isActive && (
                    <button
                      onClick={() => makePrimary(r._id)}
                      className="px-3 py-1.5 rounded-lg border border-slate-300 dark:border-slate-600 text-xs font-semibold"
                    >
                      Make primary
                    </button>
                  )}
                  <button
                    onClick={() => setActive(r._id, !r.isActive)}
                    className="px-3 py-1.5 rounded-lg border border-slate-300 dark:border-slate-600 text-xs font-semibold"
                  >
                    {r.isActive ? 'Deactivate' : 'Reactivate'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        <p className="text-xs text-slate-500">
          A GSTIN cannot be edited once saved — it determines which state every past filing belonged
          to. Add a new registration and deactivate the old one instead.
        </p>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <form
            onSubmit={handleCreate}
            className="bg-white dark:bg-slate-900 rounded-2xl p-6 w-full max-w-lg space-y-4 max-h-[90vh] overflow-y-auto"
          >
            <h3 className="font-bold text-lg">Add GST registration</h3>

            {error && (
              <div className="p-3 rounded-lg bg-danger/10 dark:bg-danger border border-danger/25 dark:border-danger/40 text-danger-text dark:text-danger-text text-sm">
                {error}
              </div>
            )}

            <div>
              <label className="text-xs font-semibold block mb-1">GSTIN</label>
              <input
                value={gstin}
                onChange={(e) => setGstin(e.target.value.toUpperCase())}
                required
                maxLength={15}
                placeholder="27AAAAA0000A1Z5"
                className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-transparent font-mono text-sm"
              />
              <p className="text-[11px] text-slate-500 mt-1">
                Checked against the GSTIN check digit — the state code is taken from it.
              </p>
            </div>

            <div>
              <label className="text-xs font-semibold block mb-1">Legal name</label>
              <input
                value={legalName}
                onChange={(e) => setLegalName(e.target.value)}
                required
                className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-transparent text-sm"
              />
            </div>

            <div>
              <label className="text-xs font-semibold block mb-1">Trade name (optional)</label>
              <input
                value={tradeName}
                onChange={(e) => setTradeName(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-transparent text-sm"
              />
            </div>

            <div>
              <label className="text-xs font-semibold block mb-1">
                Annual gross turnover
              </label>
              <input
                type="number"
                min="0"
                value={grossTurnover}
                onChange={(e) => setGrossTurnover(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-transparent text-sm"
              />
              <p className="text-[11px] text-slate-500 mt-1">
                Declared on the return header. Left as entered — never estimated from invoices.
              </p>
            </div>

            <div>
              <label className="text-xs font-semibold block mb-1">Branches filing under this GSTIN</label>
              <div className="border border-slate-300 dark:border-slate-600 rounded-lg max-h-40 overflow-y-auto divide-y divide-slate-200 dark:divide-slate-700">
                {locations.length === 0 && (
                  <p className="p-3 text-xs text-slate-500">No branches defined yet.</p>
                )}
                {locations.map((l) => (
                  <label key={l._id} className="flex items-center gap-2 p-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={branchIds.includes(l._id)}
                      onChange={(e) =>
                        setBranchIds((prev) =>
                          e.target.checked ? [...prev, l._id] : prev.filter((x) => x !== l._id))
                      }
                    />
                    {l.name}
                  </label>
                ))}
              </div>
              <p className="text-[11px] text-slate-500 mt-1">
                A branch belongs to one registration. Leave empty for a single-state business.
              </p>
            </div>

            <div className="flex gap-2 justify-end pt-2">
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 text-sm font-semibold"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 rounded-lg bg-primary text-on-primary text-sm font-semibold"
              >
                Add registration
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

export default GstRegistrationsSettingsPage;
